import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import {
  HiOutlineUser,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineRefresh,
  HiOutlinePlus,
  HiOutlineChevronDown,
  HiOutlineGlobe,
  HiOutlineServer,
  HiOutlineCog,
  HiOutlineViewList,
  HiOutlineLightningBolt,
  HiOutlineClock,
  HiOutlineDatabase,
  HiOutlineKey,
  HiOutlineChip,
  HiOutlineMail,
  HiOutlineX,
  HiOutlineArrowLeft,
} from 'react-icons/hi';
import {
  getTeamMember,
  deleteTeamMember,
  updateTeamMember,
  getCloudRunServicesList,
  getAllVMInstances,
  getCloudRunJobs,
  getTaskQueues,
  getCloudBuildTriggersLight,
  getSchedulerJobs,
  getBuckets,
  getServiceAccounts,
  getPubSubTopics,
} from '../services/api';
import selectStyles from '../shared/selectStyles';
import AddTeamMemberModal from '../components/AddTeamMemberModal';
import './TeamMemberDetail.css';

const TYPE_META = {
  cloud_run:        { label: 'Cloud Run Services',       icon: HiOutlineGlobe,          color: 'var(--accent-blue)' },
  vm_instance:      { label: 'VM Instances',             icon: HiOutlineServer,         color: 'var(--accent-green, #22c55e)' },
  cloud_run_job:    { label: 'Cloud Run Jobs',           icon: HiOutlineCog,            color: 'var(--accent-purple, #a855f7)' },
  cloud_task:       { label: 'Cloud Task Queues',        icon: HiOutlineViewList,       color: 'var(--accent-orange, #f97316)' },
  cloud_build:      { label: 'Cloud Build Triggers',     icon: HiOutlineLightningBolt,  color: 'var(--accent-yellow, #eab308)' },
  cloud_scheduler:  { label: 'Cloud Scheduler Jobs',     icon: HiOutlineClock,          color: 'var(--accent-teal, #14b8a6)' },
  bucket:           { label: 'GCS Buckets',              icon: HiOutlineDatabase,       color: 'var(--accent-pink, #ec4899)' },
  service_account:  { label: 'Service Accounts',         icon: HiOutlineKey,            color: 'var(--accent-indigo, #6366f1)' },
  gemini_api:       { label: 'Gemini API',               icon: HiOutlineChip,           color: 'var(--accent-rose, #f43f5e)' },
  pub_sub:          { label: 'Pub/Sub Topics',           icon: HiOutlineMail,           color: 'var(--accent-cyan, #06b6d4)' },
};

const LIVE_FETCHERS = {
  cloud_run:       (pid) => getCloudRunServicesList(pid).then(r => r.data),
  vm_instance:     (pid) => getAllVMInstances(pid).then(r => r.data),
  cloud_run_job:   (pid) => getCloudRunJobs(pid).then(r => r.data),
  cloud_task:      (pid) => getTaskQueues(pid).then(r => r.data),
  cloud_build:     (pid) => getCloudBuildTriggersLight(pid).then(r => r.data),
  cloud_scheduler: (pid) => getSchedulerJobs(pid).then(r => r.data),
  bucket:          (pid) => getBuckets(pid).then(r => r.data),
  service_account: (pid) => getServiceAccounts(pid).then(r => r.data),
  pub_sub:         (pid) => getPubSubTopics(pid).then(r => r.data),
};

const PICKER_FETCHERS = {
  cloud_run: (pid) => getCloudRunServicesList(pid).then(r => r.data.map(s => ({ value: s.name, label: `${s.name} (${s.region})`, region: s.region }))),
  vm_instance: (pid) => getAllVMInstances(pid).then(r => r.data.map(v => ({ value: v.name, label: `${v.name} (${v.zone})`, region: v.zone }))),
  cloud_run_job: (pid) => getCloudRunJobs(pid).then(r => r.data.map(j => ({ value: j.name, label: `${j.name} (${j.region})`, region: j.region }))),
  cloud_task: (pid) => getTaskQueues(pid).then(r => r.data.map(q => ({ value: q.name, label: `${q.name} (${q.region})`, region: q.region }))),
  cloud_build: (pid) => getCloudBuildTriggersLight(pid).then(r => r.data.map(t => ({ value: t.name || t.id, label: t.region && t.region !== 'global' ? `${t.name || t.id} (${t.region})` : (t.name || t.id), region: t.region || '' }))),
  cloud_scheduler: (pid) => getSchedulerJobs(pid).then(r => r.data.map(j => ({ value: j.name, label: `${j.name} (${j.region})`, region: j.region }))),
  bucket: (pid) => getBuckets(pid).then(r => r.data.map(b => ({ value: b.name, label: `${b.name} (${b.location})`, region: b.location }))),
  service_account: (pid) => getServiceAccounts(pid).then(r => r.data.map(a => ({ value: a.email, label: a.email, region: '' }))),
  pub_sub: (pid) => getPubSubTopics(pid).then(r => r.data.map(t => ({ value: t.name, label: t.name, region: '' }))),
};

function TeamMemberDetail({ gcpProjects, onMemberUpdated }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddServicePanel, setShowAddServicePanel] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [liveData, setLiveData] = useState({});
  const [liveLoading, setLiveLoading] = useState({});
  const [addingToSection, setAddingToSection] = useState(null);

  const fetchMember = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTeamMember(id);
      setMember(res.data);
    } catch {
      toast.error('Failed to load team member');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { fetchMember(); }, [fetchMember]);

  const groupedServices = useMemo(() => {
    if (!member?.services) return {};
    const groups = {};
    for (const svc of member.services) {
      if (!groups[svc.type]) groups[svc.type] = [];
      groups[svc.type].push(svc);
    }
    return groups;
  }, [member]);

  const typeCounts = useMemo(() => {
    const counts = {};
    for (const [type, svcs] of Object.entries(groupedServices)) {
      counts[type] = svcs.length;
    }
    return counts;
  }, [groupedServices]);

  const toggleSection = (type) => {
    setExpandedSections(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const fetchLiveStatus = useCallback(async (type, svcs) => {
    const projectIds = [...new Set(svcs.map(s => s.gcpProjectId))];
    const fetcher = LIVE_FETCHERS[type];
    if (!fetcher) return;

    const key = type;
    setLiveLoading(prev => ({ ...prev, [key]: true }));
    try {
      const allResults = await Promise.all(projectIds.map(pid => fetcher(pid).catch(() => [])));
      const flat = allResults.flat();
      setLiveData(prev => ({ ...prev, [key]: flat }));
    } catch {
      // silent
    }
    setLiveLoading(prev => ({ ...prev, [key]: false }));
  }, []);

  useEffect(() => {
    if (!member?.services) return;
    const allExpanded = Object.keys(expandedSections).filter(k => expandedSections[k]);
    for (const type of allExpanded) {
      if (!liveData[type] && groupedServices[type]) {
        fetchLiveStatus(type, groupedServices[type]);
      }
    }
  }, [expandedSections, member, groupedServices, liveData, fetchLiveStatus]);

  const handleDelete = async () => {
    if (!window.confirm(`Delete team member "${member.name}"?`)) return;
    try {
      await deleteTeamMember(id);
      toast.success('Team member deleted');
      onMemberUpdated();
      navigate('/');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleEdited = (updated) => {
    setMember(updated);
    setShowEdit(false);
    setLiveData({});
    onMemberUpdated();
  };

  const handleInlineAdd = async (type, newEntries) => {
    const updatedServices = [...(member.services || []), ...newEntries];
    try {
      const res = await updateTeamMember(id, { services: updatedServices });
      setMember(res.data);
      setAddingToSection(null);
      setLiveData(prev => { const c = { ...prev }; delete c[type]; return c; });
      onMemberUpdated();
      toast.success(`Added ${newEntries.length} service${newEntries.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to add services');
    }
  };

  const handleTopLevelAdd = async (newEntries) => {
    const updatedServices = [...(member.services || []), ...newEntries];
    try {
      const res = await updateTeamMember(id, { services: updatedServices });
      setMember(res.data);
      setShowAddServicePanel(false);
      setLiveData({});
      onMemberUpdated();
      toast.success(`Added ${newEntries.length} service${newEntries.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to add services');
    }
  };

  const handleRemoveService = async (svcIdx) => {
    const svc = member.services[svcIdx];
    if (!window.confirm(`Remove "${svc.name}" from ${member.name}?`)) return;
    const updatedServices = member.services.filter((_, i) => i !== svcIdx);
    try {
      const res = await updateTeamMember(id, { services: updatedServices });
      setMember(res.data);
      setLiveData({});
      onMemberUpdated();
      toast.success(`Removed "${svc.name}"`);
    } catch {
      toast.error('Failed to remove service');
    }
  };

  const matchLive = (svc, liveList) => {
    if (!liveList) return null;
    return liveList.find(l => {
      const lName = l.name || l.email || '';
      return lName === svc.name;
    });
  };

  if (loading) {
    return (
      <div className="team-detail">
        <div className="loading-page">
          <div className="spinner"></div>
          <span>Loading team member…</span>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="team-detail">
        <div className="empty-state">Team member not found.</div>
      </div>
    );
  }

  return (
    <div className="team-detail">
      <div className="team-detail-header">
        <div className="team-detail-info">
          <button className="team-back-btn" onClick={() => navigate('/team')} title="Back to Team">
            <HiOutlineArrowLeft size={20} />
          </button>
          <div className="team-avatar">
            <HiOutlineUser size={28} />
          </div>
          <div>
            <h1>{member.name}</h1>
            <span className="team-detail-sub">
              {member.services?.length || 0} service{member.services?.length !== 1 ? 's' : ''} managed
            </span>
          </div>
        </div>
        <div className="team-detail-actions">
          <button
            className="btn btn-primary btn-with-icon"
            onClick={() => setShowAddServicePanel(!showAddServicePanel)}
          >
            <HiOutlinePlus size={15} /> Add Service
          </button>
          <button className="btn btn-secondary btn-icon" onClick={() => setShowEdit(true)} title="Edit">
            <HiOutlinePencil size={16} />
          </button>
          <button className="btn btn-secondary btn-icon btn-danger-hover" onClick={handleDelete} title="Delete">
            <HiOutlineTrash size={16} />
          </button>
        </div>
      </div>

      {showAddServicePanel && (
        <div className="team-top-adder-wrap">
          <TopLevelServiceAdder
            gcpProjects={gcpProjects}
            existingServices={member.services || []}
            onAdd={handleTopLevelAdd}
            onCancel={() => setShowAddServicePanel(false)}
          />
        </div>
      )}

      {Object.keys(typeCounts).length > 0 && (
        <div className="team-summary-cards">
          {Object.entries(typeCounts).map(([type, count]) => {
            const meta = TYPE_META[type] || { label: type, icon: HiOutlineCog, color: 'var(--text-muted)' };
            const Icon = meta.icon;
            return (
              <div key={type} className="team-summary-card" style={{ borderLeftColor: meta.color }}>
                <Icon size={18} style={{ color: meta.color }} />
                <div className="team-summary-card-body">
                  <span className="team-summary-count">{count}</span>
                  <span className="team-summary-label">{meta.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="team-service-sections">
        {Object.entries(groupedServices).map(([type, svcs]) => {
          const meta = TYPE_META[type] || { label: type, icon: HiOutlineCog, color: 'var(--text-muted)' };
          const Icon = meta.icon;
          const isExpanded = expandedSections[type];
          const live = liveData[type];
          const isLiveLoading = liveLoading[type];
          const isAdding = addingToSection === type;

          return (
            <div key={type} className="team-svc-section">
              <div className="team-svc-section-header" onClick={() => toggleSection(type)}>
                <div className="team-svc-section-left">
                  <Icon size={18} style={{ color: meta.color }} />
                  <span className="team-svc-section-title">{meta.label}</span>
                  <span className="badge">{svcs.length}</span>
                </div>
                <HiOutlineChevronDown
                  size={16}
                  className={`team-chevron ${isExpanded ? 'expanded' : ''}`}
                />
              </div>

              {isExpanded && (
                <div className="team-svc-section-body">
                  <div className="team-section-toolbar">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => { e.stopPropagation(); setAddingToSection(isAdding ? null : type); }}
                    >
                      <HiOutlinePlus size={13} /> Add New
                    </button>
                    {isLiveLoading && (
                      <div className="loading-inline" style={{ padding: 0 }}>
                        <div className="spinner" style={{ width: 16, height: 16 }}></div>
                        <span>Fetching…</span>
                      </div>
                    )}
                    {!isLiveLoading && live && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={(e) => { e.stopPropagation(); setLiveData(prev => { const c = { ...prev }; delete c[type]; return c; }); fetchLiveStatus(type, svcs); }}
                      >
                        <HiOutlineRefresh size={13} /> Refresh
                      </button>
                    )}
                  </div>

                  {isAdding && (
                    <InlineServiceAdder
                      type={type}
                      gcpProjects={gcpProjects}
                      existingServices={svcs}
                      onAdd={(entries) => handleInlineAdd(type, entries)}
                      onCancel={() => setAddingToSection(null)}
                    />
                  )}

                  {(() => {
                    const byProject = {};
                    svcs.forEach(svc => {
                      const key = svc.gcpProjectId || '_none';
                      if (!byProject[key]) byProject[key] = { name: svc.gcpProjectName || 'Unknown', items: [] };
                      byProject[key].items.push(svc);
                    });
                    const projectKeys = Object.keys(byProject);
                    const multiProject = projectKeys.length > 1;

                    return projectKeys.map(pKey => (
                      <div key={pKey} className={`team-project-group ${multiProject ? 'multi' : ''}`}>
                        {multiProject && (
                          <div className="team-project-group-header">
                            <HiOutlineServer size={14} />
                            <span>{byProject[pKey].name}</span>
                            <span className="badge">{byProject[pKey].items.length}</span>
                          </div>
                        )}
                        <div className="team-svc-grid">
                          {byProject[pKey].items.map((svc, idx) => {
                            const globalIdx = member.services.indexOf(svc);
                            const liveInfo = matchLive(svc, live);
                            return (
                              <ServiceCard
                                key={`${svc.name}-${svc.gcpProjectId}-${idx}`}
                                svc={svc}
                                type={type}
                                liveInfo={liveInfo}
                                meta={meta}
                                onRemove={() => handleRemoveService(globalIdx)}
                                showProject={!multiProject}
                              />
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(groupedServices).length === 0 && (
          <div className="empty-state">
            No services assigned yet. Click edit to add services.
          </div>
        )}
      </div>

      {showEdit && (
        <AddTeamMemberModal
          gcpProjects={gcpProjects}
          onClose={() => setShowEdit(false)}
          onCreated={handleEdited}
          editMember={member}
        />
      )}
    </div>
  );
}

function ServiceCard({ svc, type, liveInfo, meta, onRemove, showProject = true }) {
  const statusText = getStatusText(type, liveInfo);
  const statusClass = getStatusClass(type, liveInfo);

  return (
    <div className="team-svc-card">
      <div className="team-svc-card-header">
        <span className="team-svc-card-name" title={svc.name}>{svc.name}</span>
        <div className="team-svc-card-header-right">
          {statusText && (
            <span className={`team-svc-status ${statusClass}`}>{statusText}</span>
          )}
          <button className="team-card-remove-btn" onClick={onRemove} title="Remove">
            <HiOutlineTrash size={13} />
          </button>
        </div>
      </div>
      <div className="team-svc-card-details">
        {showProject && <DetailRow label="Project" value={svc.gcpProjectName} />}
        {svc.region && <DetailRow label="Region" value={svc.region} />}
        {type === 'cloud_run' && liveInfo && (
          <>
            {liveInfo.latestRevision && <DetailRow label="Revision" value={liveInfo.latestRevision} />}
          </>
        )}
        {type === 'vm_instance' && liveInfo && (
          <>
            {liveInfo.machineType && <DetailRow label="Machine" value={liveInfo.machineType} />}
            {liveInfo.internalIP && <DetailRow label="Internal IP" value={liveInfo.internalIP} />}
            {liveInfo.externalIP && <DetailRow label="External IP" value={liveInfo.externalIP} />}
          </>
        )}
        {type === 'cloud_scheduler' && liveInfo && (
          <>
            {liveInfo.schedule && <DetailRow label="Schedule" value={liveInfo.schedule} />}
            {liveInfo.nextRunTime && <DetailRow label="Next Run" value={new Date(liveInfo.nextRunTime).toLocaleString()} />}
          </>
        )}
        {type === 'cloud_task' && liveInfo && (
          <>
            {liveInfo.state && <DetailRow label="State" value={liveInfo.state} />}
          </>
        )}
        {type === 'bucket' && liveInfo && (
          <>
            {liveInfo.location && <DetailRow label="Location" value={liveInfo.location} />}
            {liveInfo.storageClass && <DetailRow label="Class" value={liveInfo.storageClass} />}
          </>
        )}
        {type === 'service_account' && liveInfo && (
          <>
            <DetailRow label="Email" value={liveInfo.email} />
            <DetailRow label="Status" value={liveInfo.disabled ? 'Disabled' : 'Active'} />
          </>
        )}
        {type === 'gemini_api' && svc.meta && (
          <>
            {svc.meta.endpoint && <DetailRow label="Endpoint" value={svc.meta.endpoint} />}
            {svc.meta.model && <DetailRow label="Model" value={svc.meta.model} />}
          </>
        )}
        {type === 'pub_sub' && liveInfo && (
          <DetailRow label="Topic" value={liveInfo.fullName || svc.name} />
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="team-detail-row">
      <span className="team-detail-label">{label}</span>
      <span className="team-detail-value" title={value}>{value}</span>
    </div>
  );
}

const SERVICE_TYPE_OPTIONS = [
  { value: 'cloud_run', label: 'Cloud Run Service' },
  { value: 'vm_instance', label: 'VM Instance' },
  { value: 'cloud_run_job', label: 'Cloud Run Job' },
  { value: 'cloud_task', label: 'Cloud Task Queue' },
  { value: 'cloud_build', label: 'Cloud Build Trigger' },
  { value: 'cloud_scheduler', label: 'Cloud Scheduler Job' },
  { value: 'bucket', label: 'GCS Bucket' },
  { value: 'service_account', label: 'Service Account' },
  { value: 'gemini_api', label: 'Gemini API' },
  { value: 'pub_sub', label: 'Pub/Sub Topic' },
];

function TopLevelServiceAdder({ gcpProjects, existingServices, onAdd, onCancel }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geminiEndpoint, setGeminiEndpoint] = useState('');
  const [geminiModel, setGeminiModel] = useState('');

  const projectOptions = gcpProjects.map(p => ({
    value: p._id,
    label: `${p.projectName} (${p.projectId})`,
    projectName: p.projectName,
  }));

  useEffect(() => {
    if (!selectedProject || !selectedType || selectedType.value === 'gemini_api') return;
    let cancelled = false;
    setLoadingServices(true);
    setAvailableServices([]);
    setSelectedServices([]);
    const fetcher = PICKER_FETCHERS[selectedType.value];
    if (fetcher) {
      fetcher(selectedProject.value)
        .then(opts => {
          if (cancelled) return;
          const assignedForProject = new Set(
            existingServices
              .filter(s => s.type === selectedType.value && s.gcpProjectId === selectedProject.value)
              .map(s => s.name)
          );
          const filtered = opts
            .filter(o => !assignedForProject.has(o.value))
            .sort((a, b) => a.label.localeCompare(b.label));
          setAvailableServices(filtered);
        })
        .catch(() => { if (!cancelled) toast.error('Failed to fetch services'); })
        .finally(() => { if (!cancelled) setLoadingServices(false); });
    }
    return () => { cancelled = true; };
  }, [selectedProject, selectedType, existingServices]);

  const handleAdd = async () => {
    if (selectedType.value === 'gemini_api') {
      if (!geminiEndpoint.trim()) { toast.error('Endpoint is required'); return; }
      setSaving(true);
      await onAdd([{
        type: 'gemini_api',
        name: geminiModel.trim() || 'Gemini API',
        region: '',
        gcpProjectId: selectedProject.value,
        gcpProjectName: selectedProject.projectName,
        meta: { endpoint: geminiEndpoint.trim(), model: geminiModel.trim() },
      }]);
      setSaving(false);
      return;
    }
    if (selectedServices.length === 0) { toast.error('Select at least one service'); return; }
    setSaving(true);
    const entries = selectedServices.map(s => ({
      type: selectedType.value,
      name: s.value,
      region: s.region || '',
      gcpProjectId: selectedProject.value,
      gcpProjectName: selectedProject.projectName,
      meta: {},
    }));
    await onAdd(entries);
    setSaving(false);
  };

  return (
    <div className="team-inline-adder team-top-adder">
      <div className="team-inline-adder-header">
        <span>Add Service</span>
        <button className="team-card-remove-btn" onClick={onCancel}><HiOutlineX size={14} /></button>
      </div>
      <div className="team-top-adder-body">
        <div className="team-top-adder-fields">
          <div className="team-inline-field">
            <label>Project</label>
            <Select
              styles={selectStyles}
              options={projectOptions}
              value={selectedProject}
              onChange={(v) => { setSelectedProject(v); setSelectedType(null); }}
              placeholder="Select project..."
              isClearable
              menuPortalTarget={document.body}
            />
          </div>

          {selectedProject && (
            <div className="team-inline-field">
              <label>Service Type</label>
              <Select
                styles={selectStyles}
                options={SERVICE_TYPE_OPTIONS}
                value={selectedType}
                onChange={setSelectedType}
                placeholder="Select type..."
                isClearable
                menuPortalTarget={document.body}
              />
            </div>
          )}

          {selectedProject && selectedType && selectedType.value === 'gemini_api' && (
            <>
              <div className="team-inline-field">
                <label>Endpoint</label>
                <input type="text" placeholder="https://..." value={geminiEndpoint} onChange={e => setGeminiEndpoint(e.target.value)} />
              </div>
              <div className="team-inline-field">
                <label>Model (optional)</label>
                <input type="text" placeholder="e.g. gemini-1.5-pro" value={geminiModel} onChange={e => setGeminiModel(e.target.value)} />
              </div>
            </>
          )}

          {selectedProject && selectedType && selectedType.value !== 'gemini_api' && (
            <div className="team-inline-field team-inline-field-grow">
              <label>Services</label>
              {loadingServices ? (
                <div className="loading-inline" style={{ padding: '6px 0' }}>
                  <div className="spinner" style={{ width: 16, height: 16 }}></div>
                  <span>Loading…</span>
                </div>
              ) : (
                <Select
                  styles={selectStyles}
                  options={availableServices}
                  value={selectedServices}
                  onChange={setSelectedServices}
                  placeholder="Select services..."
                  isMulti
                  closeMenuOnSelect={false}
                  noOptionsMessage={() => 'No new services available'}
                  menuPortalTarget={document.body}
                />
              )}
            </div>
          )}
        </div>

        {selectedProject && selectedType && (
          <div className="team-inline-adder-actions">
            <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

function InlineServiceAdder({ type, gcpProjects, existingServices, onAdd, onCancel }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geminiEndpoint, setGeminiEndpoint] = useState('');
  const [geminiModel, setGeminiModel] = useState('');

  const projectOptions = gcpProjects.map(p => ({
    value: p._id,
    label: `${p.projectName} (${p.projectId})`,
    projectName: p.projectName,
  }));

  useEffect(() => {
    if (!selectedProject || type === 'gemini_api') return;
    let cancelled = false;
    setLoadingServices(true);
    setAvailableServices([]);
    setSelectedServices([]);
    const fetcher = PICKER_FETCHERS[type];
    if (fetcher) {
      fetcher(selectedProject.value)
        .then(opts => {
          if (cancelled) return;
          const assignedForProject = new Set(
            existingServices
              .filter(s => s.gcpProjectId === selectedProject.value)
              .map(s => s.name)
          );
          const filtered = opts
            .filter(o => !assignedForProject.has(o.value))
            .sort((a, b) => a.label.localeCompare(b.label));
          setAvailableServices(filtered);
        })
        .catch(() => { if (!cancelled) toast.error('Failed to fetch services'); })
        .finally(() => { if (!cancelled) setLoadingServices(false); });
    }
    return () => { cancelled = true; };
  }, [selectedProject, type, existingServices]);

  const handleAdd = async () => {
    if (type === 'gemini_api') {
      if (!geminiEndpoint.trim()) { toast.error('Endpoint is required'); return; }
      setSaving(true);
      await onAdd([{
        type: 'gemini_api',
        name: geminiModel.trim() || 'Gemini API',
        region: '',
        gcpProjectId: selectedProject.value,
        gcpProjectName: selectedProject.projectName,
        meta: { endpoint: geminiEndpoint.trim(), model: geminiModel.trim() },
      }]);
      setSaving(false);
      return;
    }
    if (selectedServices.length === 0) { toast.error('Select at least one service'); return; }
    setSaving(true);
    const entries = selectedServices.map(s => ({
      type,
      name: s.value,
      region: s.region || '',
      gcpProjectId: selectedProject.value,
      gcpProjectName: selectedProject.projectName,
      meta: {},
    }));
    await onAdd(entries);
    setSaving(false);
  };

  return (
    <div className="team-inline-adder">
      <div className="team-inline-adder-header">
        <span>Add {TYPE_META[type]?.label || type}</span>
        <button className="team-card-remove-btn" onClick={onCancel}><HiOutlineX size={14} /></button>
      </div>
      <div className="team-inline-adder-body">
        <div className="team-inline-field">
          <label>Project</label>
          <Select
            styles={selectStyles}
            options={projectOptions}
            value={selectedProject}
            onChange={setSelectedProject}
            placeholder="Select project..."
            isClearable
            menuPortalTarget={document.body}
          />
        </div>

        {selectedProject && type === 'gemini_api' && (
          <>
            <div className="team-inline-field">
              <label>Endpoint</label>
              <input type="text" placeholder="https://..." value={geminiEndpoint} onChange={e => setGeminiEndpoint(e.target.value)} />
            </div>
            <div className="team-inline-field">
              <label>Model (optional)</label>
              <input type="text" placeholder="e.g. gemini-1.5-pro" value={geminiModel} onChange={e => setGeminiModel(e.target.value)} />
            </div>
          </>
        )}

        {selectedProject && type !== 'gemini_api' && (
          <div className="team-inline-field">
            <label>Services</label>
            {loadingServices ? (
              <div className="loading-inline" style={{ padding: '6px 0' }}>
                <div className="spinner" style={{ width: 16, height: 16 }}></div>
                <span>Loading…</span>
              </div>
            ) : (
              <Select
                styles={selectStyles}
                options={availableServices}
                value={selectedServices}
                onChange={setSelectedServices}
                placeholder="Select services..."
                isMulti
                closeMenuOnSelect={false}
                noOptionsMessage={() => 'No new services available'}
                menuPortalTarget={document.body}
              />
            )}
          </div>
        )}

        {selectedProject && (
          <div className="team-inline-adder-actions">
            <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

function getStatusText(type, liveInfo) {
  if (!liveInfo) return null;
  if (type === 'cloud_run') {
    const cond = liveInfo.conditions?.find(c => c.type === 'Ready');
    return cond?.status === 'True' ? 'Ready' : cond?.status === 'False' ? 'Failed' : 'Unknown';
  }
  if (type === 'vm_instance') return liveInfo.status || null;
  if (type === 'cloud_run_job') return liveInfo.latestExecution?.completionStatus || 'Idle';
  if (type === 'cloud_task') return liveInfo.state || null;
  if (type === 'cloud_build') return liveInfo.lastBuildStatus || null;
  if (type === 'cloud_scheduler') return liveInfo.lastRunStatus || liveInfo.state || null;
  if (type === 'service_account') return liveInfo.disabled ? 'Disabled' : 'Active';
  return null;
}

function getStatusClass(type, liveInfo) {
  const text = getStatusText(type, liveInfo);
  if (!text) return '';
  const lower = text.toLowerCase();
  if (['ready', 'running', 'active', 'enabled', 'succeeded'].includes(lower)) return 'status-ok';
  if (['failed', 'error', 'terminated', 'disabled', 'stopped'].includes(lower)) return 'status-error';
  return 'status-warn';
}

export default TeamMemberDetail;
